import React, {useEffect, useState} from "react";
import { Box, Card, Button, IconButton, Collapse } from "@mui/material";
import Form from "react-bootstrap/Form";
import TextField from "@mui/material/TextField";
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import frLocale from "date-fns/locale/fr";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { useTheme } from '@mui/material/styles';
import { useSidebar } from "../Context/SidebarContext";

const FaosFilterCard = ({ filters, onFilterChange }) => {
  const [showAllFields, setShowAllFields] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);
  const theme = useTheme();
  const { isOpen } = useSidebar();

  const filterPos = {
    position: "fixed",
    top: "20px",
    width: "22%", 
    maxHeight: "90vh",
    transition: "all 0.3s ease", 
    padding: "20px",  
    backgroundColor: "#fff",    
    '& .MuiFormGroup-root': {
      marginBottom: '1rem'
    },
    '& .MuiTextField-root': {
      marginBottom: '0.5rem'
    },

    [theme.breakpoints.down('lg')]: {
      width: "28%",
      marginRight: "5px"
    },
    [theme.breakpoints.down('md')]: {
      position: "inherit",
      width: "100%"
    },
    [theme.breakpoints.down('sm')]: {
      position: "inherit",
      width: "100%"
    },
  };

  const openFilterPos = {
    position: "fixed",
    top: "20px",
    width: "19%", 
    maxHeight: "90vh",
    transition: "all 0.3s ease", 
    padding: "20px",  
    backgroundColor: "#fff", 
    '& .MuiFormGroup-root': {
      marginBottom: '1rem'
    },
    '& .MuiTextField-root': {
      marginBottom: '0.5rem'
    },

    [theme.breakpoints.down('lg')]: {
      width: "100%",
      position: "inherit"
    },
    [theme.breakpoints.down('md')]: {
      position: "inherit",
      width: "100%"
    },
    [theme.breakpoints.down('sm')]: {
      position: "inherit",
      width: "100%"
    },
  };

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);
  const handleChange = (field, value) => {
    const newFilters = { ...tempFilters };

    if (field === "startDate" || field === "endDate") {
      newFilters.dateRange = {
        ...newFilters.dateRange,
        [field]: value
      };
    } else {
      newFilters[field] = value;
    }

    newFilters.isEmpty = !Object.values(newFilters).some(val =>
        val && typeof val === 'string' ? val.trim() !== '' : val !== null
    );

    // Mettre à jour l'état local uniquement, sans appeler onFilterChange
    setTempFilters(newFilters);
  };

  const applyFilters = () => {
    onFilterChange(tempFilters);
  };

  const onClickClean = () => {
    const emptyFilters = {
      reference: "",
      title: "",
      municipality: "",
      canton: "",
      status: "",
      type_code: "",
      owner: "",
      address: "",
      dateRange: {
        startDate: null,
        endDate: null
      },
      isEmpty: true
    };
    setTempFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  // Liste des codes types disponibles
  const typeCodeOptions = [
    { value: "APA", label: "Autorisation procédure accélérée" },
    { value: "DD", label: "Demande définitive" },
    { value: "M", label: "Demande de modification" },
    { value: "DR", label: "Demande de renseignement" }
  ];

  return (
    <Card
      className="shadow-none"
      sx={isOpen ? openFilterPos : filterPos}
    >
      <Box component="form">

        <Form.Group className="my-2" key="type_code">
          <FormControl fullWidth size="small">
            <InputLabel>Code Type</InputLabel>
            <Select
              value={tempFilters.type_code || ""}
              label="Code Type"
              onChange={(e) => handleChange("type_code", e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              {typeCodeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value} - {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Form.Group>

        <Form.Group className="my-2 py-2" key="title">
          <TextField
            type="text"
            label="Travaux"
            placeholder="Description des travaux"
            fullWidth
            value={tempFilters.title}
            onChange={(e) => handleChange("title", e.target.value)}
            size="small"
            multiline
            rows={2}
          />
        </Form.Group>

        <IconButton
          onClick={() => setShowAllFields(!showAllFields)}
          sx={{ "&:hover": { background: "none" }, p: 0 }}
        >
          {showAllFields ? 
            <><span style={{fontSize: "14px"}}>Afficher moins</span> <ExpandLessIcon /></> : 
            <><span style={{fontSize: "14px"}}>Filtre avancé</span><ExpandMoreIcon /></>
          }
        </IconButton>

        <Collapse in={showAllFields} timeout="auto" unmountOnExit>
          <Form.Group className="my-2" key="municipality">
            <TextField
              type="text"
              label="Commune"
              placeholder="Commune"
              fullWidth
              value={tempFilters.municipality}
              onChange={(e) => handleChange("municipality", e.target.value)}
              size="small"
            />
          </Form.Group>

          <Form.Group className="my-2" key="canton">
            <TextField
              type="text"
              label="Canton"
              placeholder="Canton"
              fullWidth
              value={tempFilters.canton}
              onChange={(e) => handleChange("canton", e.target.value)}
              size="small"
            />
          </Form.Group>

          <Form.Group className="my-2" key="status">
            <FormControl fullWidth size="small">
              <InputLabel>Résultat</InputLabel>
              <Select
                value={tempFilters.status}
                label="Résultat"
                onChange={(e) => handleChange("status", e.target.value)}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="ACCEPTE">Accepté</MenuItem>
                <MenuItem value="REFUSE">Refusé</MenuItem>
                <MenuItem value="TERMINE">Terminé</MenuItem>
              </Select>
            </FormControl>
          </Form.Group>

          <Form.Group className="my-2" key="owner">
            <TextField
              type="text"
              label="Propriétaire"
              placeholder="Nom du propriétaire"
              fullWidth
              value={tempFilters.owner}
              onChange={(e) => handleChange("owner", e.target.value)}
              size="small"
            />
          </Form.Group>

          <Form.Group className="my-2" key="address">
            <TextField
              type="text"
              label="Adresse"
              placeholder="Adresse du bien"
              fullWidth
              value={tempFilters.address}
              onChange={(e) => handleChange("address", e.target.value)}
              size="small"
            />
          </Form.Group>

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
            <Form.Group className="my-2 pt-2">
              <DatePicker
                label="Date de début"
                value={tempFilters.dateRange.startDate}
                onChange={(date) => handleChange("startDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true
                  }
                }}
              />
            </Form.Group>

            <Form.Group className="my-2">
              <DatePicker
                label="Date de fin"
                value={tempFilters.dateRange.endDate}
                onChange={(date) => handleChange("endDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true
                  }
                }}
              />
            </Form.Group>
          </LocalizationProvider>
        </Collapse>

        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
          <Button
              sx={{
                background: "#757575",
                color: "#fff",
                "&:hover": { background: "#757575" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                verticalAlign: "center",
                fontWeight: 500
              }}
              onClick={() => onClickClean()}
          >
            <RefreshIcon sx={{mr: 1, fontSize: 18}} />
            REINITIALISER
          </Button>
          <Button
              sx={{
                background: "#299CDB",
                color: "#fff",
                "&:hover": { background: "#299CDB" },
                ml: 1,
              }}
              onClick={applyFilters}
          >
            FILTRER
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default FaosFilterCard; 